'use client';

/**
 * Browser-native EventBus using EventTarget API.
 * Provides typed, event-driven communication without WebSocket dependency.
 * When backend is ready, socket-client will relay server events through this bus.
 */

type EventCallback<T = unknown> = (data: T) => void;

interface EventEntry {
  callback: EventCallback;
  wrapper: EventListener;
}

class EventBusImpl {
  private target = new EventTarget();
  private listeners = new Map<string, Set<EventEntry>>();

  /** Subscribe to an event */
  on<T = unknown>(event: string, callback: EventCallback<T>): void {
    const wrapper = ((e: CustomEvent<T>) => {
      callback(e.detail);
    }) as EventListener;

    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add({ callback: callback as EventCallback, wrapper });
    this.target.addEventListener(event, wrapper);
  }

  /** Unsubscribe from an event */
  off<T = unknown>(event: string, callback: EventCallback<T>): void {
    const entries = this.listeners.get(event);
    if (!entries) return;
    for (const entry of entries) {
      if (entry.callback === callback) {
        this.target.removeEventListener(event, entry.wrapper);
        entries.delete(entry);
        break;
      }
    }
  }

  /** Subscribe to an event, auto-unsubscribe after first invocation */
  once<T = unknown>(event: string, callback: EventCallback<T>): void {
    const wrappedCallback: EventCallback<T> = (data) => {
      this.off(event, wrappedCallback);
      callback(data);
    };
    this.on(event, wrappedCallback);
  }

  /** Emit an event with data */
  emit<T = unknown>(event: string, data?: T): void {
    this.target.dispatchEvent(new CustomEvent(event, { detail: data }));
  }

  /** Get count of listeners for an event */
  listenerCount(event: string): number {
    return this.listeners.get(event)?.size ?? 0;
  }

  /** Remove all listeners */
  clear(): void {
    for (const [event, entries] of this.listeners) {
      for (const entry of entries) {
        this.target.removeEventListener(event, entry.wrapper);
      }
      entries.clear();
    }
    this.listeners.clear();
  }
}

/** Singleton EventBus instance for the application */
export const eventBus = new EventBusImpl();

export type { EventCallback };
